import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { NGXLogger } from 'ngx-logger';
import { combineLatest, map } from 'rxjs';

import { toSignal } from '@angular/core/rxjs-interop';
import { DocumentProvider, InboxFile, Permissions, SearchResult } from 'models';
import { DOCUMENT_PROVIDER } from 'src/app/common/tokens';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { ApplicationQuery } from 'src/app/state/application/application.query';
import { ApplicationService } from 'src/app/state/application/application.service';

import { assertExists, assertTypeByKey } from 'common';
import {
  ArchiveThumbnailSource,
  InboxThumbnailSource,
  ThumbnailSource,
} from '../thumbnail-source';

const pdfPreviewTitle = 'PDF Preview';

/** Thumbnail Cell Renderer Parameters.
 *
 * @augments ICellRendererParams
 */
export interface ThumbnailCellRendererParameters
  extends ThumbnailSource,
    ICellRendererParams<SearchResult | InboxFile> {}

/** Thumbnail Preview Cell Renderer. */
@Component({
  selector: 'app-thumbnail-preview-renderer',
  templateUrl: './thumbnail-preview-renderer.component.html',
  styleUrls: ['./thumbnail-preview-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ThumbnailPreviewRendererComponent
  implements ICellRendererAngularComp
{
  /** Observable of whether the pdf preview is open. */
  isPdfPreviewOpen = toSignal(
    combineLatest([
      this.appQuery.pdfPreviewUrl$,
      this.appQuery.rightSidebarOpen$,
    ]).pipe(
      map(([pdfPreviewUrl, rightSidebarOpen]) => {
        return (
          rightSidebarOpen &&
          !!this.pdfPreviewUrl &&
          !!pdfPreviewUrl &&
          this.pdfPreviewUrl === pdfPreviewUrl
        );
      })
    ),
    { initialValue: false }
  );
  /** Permissions for the row. */
  permissions: Permissions;

  private cellParameters: ThumbnailCellRendererParameters;
  private pdfPreviewUrl: string;

  constructor(
    private applicationService: ApplicationService,
    private appQuery: ApplicationQuery,
    private logger: NGXLogger,
    private authService: AuthenticationService,
    @Inject(DOCUMENT_PROVIDER) private documentProvider: DocumentProvider
  ) {}

  /**
   * Get the cell data cast as a search result.
   *
   * @returns The search result for the cell.
   * @throws {AssertionError} if the cell parameter data is not a search result.
   */
  get cellDataAsSearchResult(): SearchResult | undefined {
    if (!this.cellParameters.data) return undefined;
    assertTypeByKey<SearchResult>(
      this.cellParameters.data,
      'secureId',
      'string',
      'This method requires the document be a SearchResult.'
    );

    return this.cellParameters.data;
  }

  /**
   * Get the cell data cast as an inbox file.
   *
   * @returns The inbox file for the cell.
   * @throws {AssertionError} if the cell parameter data is not an inbox file.
   */
  get cellDataAsInboxFile(): InboxFile | undefined {
    if (!this.cellParameters.data) return undefined;
    assertTypeByKey<InboxFile>(
      this.cellParameters.data,
      'filename',
      'string',
      'This method requires the document be an InboxFile.'
    );

    return this.cellParameters.data;
  }

  /**
   * Get thumbnail icon.
   *
   * @returns A string of the name of the icon to be used.
   */
  get icon(): string {
    switch (this.cellParameters.data?.fileType.toLowerCase()) {
      case '.pdf':
        return 'picture_as_pdf';
      case '.tif':
      case '.tiff':
      case '.bmp':
      case '.jpeg':
      case '.jpg':
      case '.gif':
      case '.png':
      case '.dwg':
        return 'image';
      case '.dot':
      case '.doc':
      case '.docx':
        return 'file_word';
      case '.ppt':
      case '.pptx':
      case '.ppsm':
        return 'file_powerpoint';
      case '.csv':
      case '.xls':
      case '.xlsx':
        return 'file_excel';
      case '.txt':
      case '.rtf':
      case '.xml':
        return 'article';
      case '.zip':
      case '.rar':
        return 'folder_zip';
      case '.eml':
      case '.msg':
        return 'mail';
      default:
        return 'insert_drive_file';
    }
  }

  /**
   * Is the user a guest?
   *
   * @returns True if the user is a guest.
   * */
  get isGuest() {
    return this.authService.isGuest;
  }

  /**
   * Gets whether the file type is PDF.
   *
   * @returns True if the file type is PDF.
   */
  get isPdf(): boolean {
    return this.cellParameters.data?.fileType.toLowerCase() === '.pdf';
  }

  /**
   * Gets whether the file type is convertable to PDF.
   *
   * @returns True if the file type can be converted to PDF.
   */
  get isPdfConvertable(): boolean {
    if (!this.cellParameters.data) return false;
    const pdfConvertableTypes = ['.jpg', '.jpeg', '.png', '.tif', '.tiff'];
    return pdfConvertableTypes.includes(
      this.cellParameters.data.fileType.toLowerCase()
    );
  }

  /**
   * Gets whether the file type uses an SVG icon.
   *
   * Extensions that use SVG icons from the assets folder **MUST** be included in this function.
   *
   * @returns A boolean indicating if the file type uses an SVG icon.
   */
  get isSvgIcon(): boolean {
    switch (this.cellParameters.data?.fileType.toLowerCase()) {
      case '.dot':
      case '.doc':
      case '.docx':
      case '.ppt':
      case '.pptx':
      case '.ppsm':
      case '.csv':
      case '.xls':
      case '.xlsx':
        return true;
      default:
        return false;
    }
  }

  /** @inheritdoc */
  agInit(params: ThumbnailCellRendererParameters): void {
    this.loadCellParameters(params);
  }

  /**
   * Handler for click of download icon.
   *
   * @param event Mouse event.
   */
  onClickDownload(event: MouseEvent): void {
    this.logger.debug('Download icon clicked.', event);
    assertExists(
      this.cellDataAsSearchResult,
      'Cell data must be set before download can occur.'
    );
    let downloadFileUrl = '';
    if (this.cellParameters.sourceType === 'archive') {
      const archiveSource = this.cellParameters
        .source as ArchiveThumbnailSource;
      downloadFileUrl = this.documentProvider.getArchiveDownloadUrl(
        archiveSource.databaseId,
        archiveSource.archiveId,
        this.cellDataAsSearchResult.id,
        this.authService.user.token,
        this.cellDataAsSearchResult.secureId
      );
    } else {
      assertExists(
        this.cellDataAsInboxFile,
        'Inbox filename must exist in grid row data.'
      );
      const inboxSource = this.cellParameters.source as InboxThumbnailSource;
      downloadFileUrl = this.documentProvider.getInboxDownloadUrl(
        inboxSource.inboxId,
        this.cellDataAsInboxFile.filename + this.cellDataAsInboxFile.fileType
      );
    }
    window.open(downloadFileUrl, '_blank');
  }

  /**
   * Handler for the thumbnail icon button click event.
   *
   * @param event Mouse event.
   */
  onClickThumbnailIcon(event: MouseEvent): void {
    if (this.isPdfPreviewOpen()) {
      // PDF preview is already open for this doc so close it.
      this.applicationService.setPdfPreviewUrl('');
      return;
    }
    switch (this.cellParameters.sourceType) {
      case 'archive':
        this.openArchivePdfPreview();
        break;
      case 'inbox':
        this.openInboxPdfPreview();
        break;
      case 'taskSearch':
        this.openTaskSearchPdfPreview();
        break;
    }
    // prevent AG grid from seeing the click.
    event.stopPropagation();
  }

  /** @inheritdoc */
  refresh(params: ThumbnailCellRendererParameters): boolean {
    this.loadCellParameters(params);
    return true;
  }

  private loadCellParameters(params: ThumbnailCellRendererParameters): void {
    this.cellParameters = params;
    if (!this.cellParameters.data) return;
    this.permissions = this.cellParameters.data.permissions;
    if (
      this.cellParameters.sourceType === 'archive' ||
      this.cellParameters.sourceType === 'taskSearch' // Task search preview url is identical to regular archive url.
    ) {
      const archiveSource = this.cellParameters
        .source as ArchiveThumbnailSource;
      assertExists(
        this.cellDataAsSearchResult,
        'Cell data must exist as a search result.'
      );
      // TODO: Should things like this just be part of a Document class?
      this.pdfPreviewUrl = this.documentProvider.getArchivePreviewUrl(
        archiveSource.databaseId,
        archiveSource.archiveId,
        this.cellDataAsSearchResult.id,
        this.authService.user.token,
        this.cellDataAsSearchResult.secureId,
        false
      );
    } else {
      // Assume inbox since there are only two source types
      assertExists(
        this.cellDataAsInboxFile,
        ' Cell data must exist as an inbox file.'
      );
      const filename =
        this.cellDataAsInboxFile.filename + this.cellDataAsInboxFile.fileType;
      const inboxSource = this.cellParameters.source as InboxThumbnailSource;
      this.pdfPreviewUrl =
        this.isPdfConvertable || this.cellParameters.data.fileType === '.pdf'
          ? this.documentProvider.getInboxDownloadUrl(
              inboxSource.inboxId,
              filename
            )
          : this.documentProvider.getInboxPreviewUrl(
              inboxSource.inboxId,
              filename
            );
    }
    this.pdfPreviewUrl = this.updateUrlIfClientConvertable(this.pdfPreviewUrl);
  }

  /**
   * Get an archive URL and open a preview of a document.
   *
   */
  private openArchivePdfPreview(): void {
    assertExists(this.cellDataAsSearchResult, 'Row data must exist.');
    this.applicationService.setOpenDocumentToArchiveDocument(
      this.cellDataAsSearchResult.id,
      this.cellDataAsSearchResult.secureId
    );

    this.logger.debug('Preview document URL:', this.pdfPreviewUrl);
    const contentSearchTerm = this.appQuery.showContentSearchTermsInPdfPreview
      ? (this.cellDataAsSearchResult.contentSearch.terms[0] ?? '')
      : '';
    this.applicationService.setPdfPreviewUrl(
      this.pdfPreviewUrl,
      contentSearchTerm
    );
  }

  /**
   * Get an inbox URL and open a preview of a document.
   *
   */
  private openInboxPdfPreview(): void {
    assertExists(this.cellDataAsInboxFile, 'Row data must exist.');
    const inboxSource = this.cellParameters.source as InboxThumbnailSource;
    this.applicationService.setOpenDocumentToInboxDocument(
      inboxSource.inboxId,
      this.cellDataAsInboxFile.filename + this.cellDataAsInboxFile.fileType
    );
    this.applicationService.setPdfPreviewUrl(this.pdfPreviewUrl);
  }

  private openTaskSearchPdfPreview(): void {
    assertExists(this.cellDataAsSearchResult, 'Row data must exist.');
    this.applicationService.setOpenDocumentToTaskSearchDocument(
      this.cellDataAsSearchResult.id,
      this.cellDataAsSearchResult.secureId
    );

    this.logger.debug('Preview document URL:', this.pdfPreviewUrl);
    this.applicationService.setPdfPreviewUrl(this.pdfPreviewUrl);
  }

  private updateUrlIfClientConvertable(url: string) {
    assertExists(this.cellParameters.data, 'Row data must exist.');
    if (this.isPdfConvertable) {
      url +=
        '&clientConvert=' +
        this.cellParameters.data.fileType.replace('.', '').toLowerCase();
    }
    return url;
  }
}
